import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { PageHeader } from './AdminUI';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';

const defaultSiteSettings = {
    siteName: 'StatsElites',
    tagline: 'Data Consulting & MERL Services',
    description: 'Expert consulting in Monitoring, Evaluation, Research & Learning, and Statistics',
    timezone: 'UTC+3',
    dateFormat: 'DD/MM/YYYY',
    email: 'info@statselite.co.tz',
    website: 'https://www.statselite.co.tz',
    phone: '+255 749 663 117',
    address: 'Dar es Salaam, Tanzania',
    openHours: {
        weekdays: 'Monday - Friday: 8:00 AM - 5:00 PM',
        saturday: 'Saturday: 9:00 AM - 1:00 PM',
        sunday: 'Sunday: Closed'
    },
    callUs: {
        label: 'Call us',
        phone: '+255 749 663 117',
        whatsapp: '+255 749 663 117'
    },
    location: {
        name: 'StatsElites Office',
        address: 'Dar es Salaam, Tanzania',
        mapUrl: 'https://maps.google.com/maps?output=embed&q=Dar%20es%20Salaam%2C%20Tanzania&t=m&z=15'
    },
    socialLinks: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        facebook: 'https://facebook.com',
        instagram: '',
        youtube: '',
        whatsapp: 'https://wa.me/255749663117'
    },
    seo: {
        metaTitle: 'Stats Elite | Data Consulting, MERL and Analytics Training',
        metaDescription: 'Stats Elite helps organizations turn data into evidence-based decisions through MERL, statistics, analytics consulting, research support, and professional training.',
        keywords: ['MERL', 'Monitoring and Evaluation', 'Data Consulting', 'Statistics', 'Research', 'Tanzania'],
        ogImage: 'https://www.statselite.co.tz/images/slides/stats-elite-data-consulting.png',
        pages: {
            home: {
                label: 'Home',
                metaTitle: 'Stats Elite | Data Consulting, MERL and Analytics Training',
                metaDescription: 'Stats Elite helps organizations turn data into evidence-based decisions through MERL, statistics, analytics consulting, research support, and professional training.'
            },
            about: {
                label: 'About',
                metaTitle: 'About Stats Elite | MERL, Statistics and Data Consulting Experts',
                metaDescription: 'Meet the Stats Elite team supporting organizations with monitoring, evaluation, research, learning, statistics, data analytics, and evidence-based strategy.'
            },
            services: {
                label: 'Services',
                metaTitle: 'Services | MERL, Statistics, Analytics and Training | Stats Elite',
                metaDescription: 'Explore Stats Elite services in MERL, statistics, data analytics, data governance, research support, impact evaluation, dashboards, and professional training.'
            },
            blog: {
                label: 'Resources',
                metaTitle: 'Resources | Data, MERL and Analytics Insights | Stats Elite',
                metaDescription: 'Read Stats Elite resources on data consulting, MERL, statistics, analytics, research, evaluation, dashboards, and evidence-based decision making.'
            },
            contact: {
                label: 'Contact',
                metaTitle: 'Contact Stats Elite | Data Consulting and MERL Support',
                metaDescription: 'Contact Stats Elite in Dar es Salaam for MERL consulting, statistics training, data analytics, dashboards, research support, and evaluation services.'
            }
        }
    }
};

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e2e8f0',
    borderRadius: '0',
    fontSize: '14px'
};

const sectionStyle = {
    background: 'white',
    borderRadius: '0',
    padding: '24px'
};

const mergeSettings = (settings = {}) => ({
    ...defaultSiteSettings,
    ...settings,
    openHours: { ...defaultSiteSettings.openHours, ...(settings.openHours || {}) },
    callUs: { ...defaultSiteSettings.callUs, ...(settings.callUs || {}) },
    location: { ...defaultSiteSettings.location, ...(settings.location || {}) },
    socialLinks: { ...defaultSiteSettings.socialLinks, ...(settings.socialLinks || {}) },
    seo: {
        ...defaultSiteSettings.seo,
        ...(settings.seo || {}),
        pages: Object.fromEntries(
            Object.entries(defaultSiteSettings.seo.pages).map(([key, value]) => [
                key,
                { ...value, ...(settings.seo?.pages?.[key] || {}) }
            ])
        )
    }
});

const Field = ({ label, value, onChange, type = 'text', required = false, multiline = false, placeholder = '' }) => (
    <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
            {label}{required ? ' *' : ''}
        </label>
        {multiline ? (
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                rows={3}
                placeholder={placeholder}
                style={{ ...inputStyle, resize: 'vertical' }}
            />
        ) : (
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                placeholder={placeholder}
                style={inputStyle}
            />
        )}
    </div>
);

const SettingsGroup = ({ title, icon, color, children }) => (
    <div style={{ ...sectionStyle, borderTop: `4px solid ${color}` }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className={icon} style={{ color }}></i>
            {title}
        </h2>
        {children}
    </div>
);

const Settings = () => {
    const { user, apiCall } = useAuth();
    const [loading, setLoading] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [siteSettings, setSiteSettings] = useState(defaultSiteSettings);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [profileForm, setProfileForm] = useState({
        name: user?.name || '',
        email: user?.email || ''
    });

    useEffect(() => {
        setProfileForm({
            name: user?.name || '',
            email: user?.email || ''
        });
    }, [user]);

    useEffect(() => {
        fetchSiteSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSiteSettings = async () => {
        try {
            const response = await apiCall('/settings');
            const result = await response.json();
            setSiteSettings(mergeSettings(result));
        } catch (error) {
            console.error('Error loading settings:', error);
            alertError('Error', 'Error loading site settings');
        } finally {
            setSettingsLoading(false);
        }
    };

    const updateSetting = (key, value) => {
        setSiteSettings((current) => ({ ...current, [key]: value }));
    };

    const updateNestedSetting = (group, key, value) => {
        setSiteSettings((current) => ({
            ...current,
            [group]: {
                ...current[group],
                [key]: value
            }
        }));
    };

    const updateSeoSetting = (key, value) => {
        setSiteSettings((current) => ({
            ...current,
            seo: {
                ...current.seo,
                [key]: value
            }
        }));
    };

    const updateSeoPageSetting = (page, key, value) => {
        setSiteSettings((current) => ({
            ...current,
            seo: {
                ...current.seo,
                pages: {
                    ...current.seo.pages,
                    [page]: {
                        ...current.seo.pages[page],
                        [key]: value
                    }
                }
            }
        }));
    };

    const handleSiteSettingsUpdate = async (e) => {
        e.preventDefault();
        setSuccessMessage('');
        setSettingsSaving(true);

        try {
            const response = await apiCall('/settings', {
                method: 'PUT',
                body: JSON.stringify(siteSettings)
            });
            const result = await response.json();

            if (response.ok) {
                setSiteSettings(mergeSettings(result));
                setSuccessMessage('Site settings updated successfully!');
                alertSuccess('Saved', 'Site settings updated successfully.');
            } else {
                alertError('Error', result.message || 'Error updating site settings');
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            alertError('Error', 'Error updating site settings');
        } finally {
            setSettingsSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setSuccessMessage('');

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            alertError('Validation', 'New passwords do not match');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            alertError('Validation', 'Password must be at least 6 characters long');
            return;
        }

        setLoading(true);
        try {
            const response = await apiCall('/auth/change-password', {
                method: 'POST',
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword
                })
            });

            const result = await response.json();

            if (response.ok) {
                setSuccessMessage('Password changed successfully!');
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            } else {
                alertError('Error', result.message || 'Error changing password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            alertError('Error', 'Error changing password');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setSuccessMessage('');

        setLoading(true);
        try {
            const response = await apiCall(`/auth/users/${user.id}`, {
                method: 'PUT',
                body: JSON.stringify(profileForm)
            });

            const result = await response.json();

            if (response.ok) {
                setSuccessMessage('Profile updated successfully!');
            } else {
                alertError('Error', result.message || 'Error updating profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alertError('Error', 'Error updating profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Settings"
                    subtitle="Manage your account, contact details, social links, hours, and location"
                />

                {successMessage && (
                    <div className="admin-success-alert">
                        <i className="fa fa-check-circle"></i>
                        {successMessage}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                    <div style={sectionStyle}>
                        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fa fa-user" style={{ color: '#62B6E8' }}></i>
                            Profile Information
                        </h2>
                        <form onSubmit={handleProfileUpdate}>
                            <Field
                                label="Name"
                                value={profileForm.name}
                                onChange={(value) => setProfileForm({ ...profileForm, name: value })}
                            />
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={profileForm.email}
                                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                                    disabled
                                    style={{ ...inputStyle, background: '#f7fafc', color: '#a0aec0' }}
                                />
                                <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    Email cannot be changed
                                </p>
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                    Role
                                </label>
                                <div style={{
                                    padding: '10px 12px',
                                    background: '#e3f2fd',
                                    color: '#1976d2',
                                    borderRadius: '0',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textTransform: 'capitalize',
                                    display: 'inline-block'
                                }}>
                                    {user?.role}
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: '12px 24px',
                                    background: '#62B6E8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? 'Updating...' : 'Update Profile'}
                            </button>
                        </form>
                    </div>

                    <div style={sectionStyle}>
                        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fa fa-lock" style={{ color: '#f59e0b' }}></i>
                            Change Password
                        </h2>
                        <form onSubmit={handlePasswordChange}>
                            <Field
                                label="Current Password"
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(value) => setPasswordForm({ ...passwordForm, currentPassword: value })}
                                required
                            />
                            <Field
                                label="New Password"
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(value) => setPasswordForm({ ...passwordForm, newPassword: value })}
                                required
                            />
                            <Field
                                label="Confirm New Password"
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(value) => setPasswordForm({ ...passwordForm, confirmPassword: value })}
                                required
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: '12px 24px',
                                    background: '#62B6E8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? 'Changing...' : 'Change Password'}
                            </button>
                        </form>
                    </div>
                </div>

                <form onSubmit={handleSiteSettingsUpdate} style={{ marginTop: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
                            Site Configuration
                        </h2>
                        <button
                            type="submit"
                            disabled={settingsSaving || settingsLoading}
                            style={{
                                padding: '12px 24px',
                                background: settingsSaving || settingsLoading ? '#9bd3f3' : '#62B6E8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0',
                                cursor: settingsSaving || settingsLoading ? 'not-allowed' : 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            {settingsSaving ? 'Saving...' : 'Save Site Settings'}
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                        <SettingsGroup title="General Settings" icon="fa fa-cog" color="#62B6E8">
                            <Field label="Site Name" value={siteSettings.siteName} onChange={(value) => updateSetting('siteName', value)} required />
                            <Field label="Tagline" value={siteSettings.tagline} onChange={(value) => updateSetting('tagline', value)} />
                            <Field label="Description" value={siteSettings.description} onChange={(value) => updateSetting('description', value)} multiline />
                            <Field label="Website URL" type="url" value={siteSettings.website} onChange={(value) => updateSetting('website', value)} />
                            <Field label="Timezone" value={siteSettings.timezone} onChange={(value) => updateSetting('timezone', value)} />
                            <Field label="Date Format" value={siteSettings.dateFormat} onChange={(value) => updateSetting('dateFormat', value)} />
                        </SettingsGroup>

                        <SettingsGroup title="SEO Settings" icon="fa fa-search" color="#2563eb">
                            <Field label="Default Meta Title" value={siteSettings.seo.metaTitle} onChange={(value) => updateSeoSetting('metaTitle', value)} />
                            <Field label="Default Meta Description" value={siteSettings.seo.metaDescription} onChange={(value) => updateSeoSetting('metaDescription', value)} multiline />
                            <Field
                                label="Keywords"
                                value={(siteSettings.seo.keywords || []).join(', ')}
                                onChange={(value) => updateSeoSetting('keywords', value.split(',').map((item) => item.trim()).filter(Boolean))}
                                placeholder="MERL, Data Consulting, Statistics"
                            />
                            <Field label="Social Preview Image URL" type="url" value={siteSettings.seo.ogImage} onChange={(value) => updateSeoSetting('ogImage', value)} />
                            {Object.entries(siteSettings.seo.pages).map(([key, page]) => (
                                <div key={key} style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '16px' }}>
                                    <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1a202c', marginBottom: '12px' }}>
                                        {page.label || key}
                                    </h3>
                                    <Field label="Meta Title" value={page.metaTitle || ''} onChange={(value) => updateSeoPageSetting(key, 'metaTitle', value)} />
                                    <Field label="Meta Description" value={page.metaDescription || ''} onChange={(value) => updateSeoPageSetting(key, 'metaDescription', value)} multiline />
                                </div>
                            ))}
                        </SettingsGroup>

                        <SettingsGroup title="Contact Information" icon="fa fa-envelope" color="#10b981">
                            <Field label="Email" type="email" value={siteSettings.email} onChange={(value) => updateSetting('email', value)} required />
                            <Field label="Phone" value={siteSettings.phone} onChange={(value) => updateSetting('phone', value)} required />
                            <Field label="Address" value={siteSettings.address} onChange={(value) => updateSetting('address', value)} multiline />
                        </SettingsGroup>

                        <SettingsGroup title="Social Media" icon="fa fa-share-alt" color="#f59e0b">
                            <Field label="LinkedIn" type="url" value={siteSettings.socialLinks.linkedin} onChange={(value) => updateNestedSetting('socialLinks', 'linkedin', value)} />
                            <Field label="Twitter / X" type="url" value={siteSettings.socialLinks.twitter} onChange={(value) => updateNestedSetting('socialLinks', 'twitter', value)} />
                            <Field label="Facebook" type="url" value={siteSettings.socialLinks.facebook} onChange={(value) => updateNestedSetting('socialLinks', 'facebook', value)} />
                            <Field label="Instagram" type="url" value={siteSettings.socialLinks.instagram} onChange={(value) => updateNestedSetting('socialLinks', 'instagram', value)} />
                            <Field label="YouTube" type="url" value={siteSettings.socialLinks.youtube} onChange={(value) => updateNestedSetting('socialLinks', 'youtube', value)} />
                            <Field label="WhatsApp URL" type="url" value={siteSettings.socialLinks.whatsapp} onChange={(value) => updateNestedSetting('socialLinks', 'whatsapp', value)} />
                        </SettingsGroup>

                        <SettingsGroup title="Open Hours" icon="fa fa-clock-o" color="#8b5cf6">
                            <Field label="Weekdays" value={siteSettings.openHours.weekdays} onChange={(value) => updateNestedSetting('openHours', 'weekdays', value)} />
                            <Field label="Saturday" value={siteSettings.openHours.saturday} onChange={(value) => updateNestedSetting('openHours', 'saturday', value)} />
                            <Field label="Sunday" value={siteSettings.openHours.sunday} onChange={(value) => updateNestedSetting('openHours', 'sunday', value)} />
                        </SettingsGroup>

                        <SettingsGroup title="Call Us" icon="fa fa-phone" color="#ec4899">
                            <Field label="Label" value={siteSettings.callUs.label} onChange={(value) => updateNestedSetting('callUs', 'label', value)} />
                            <Field label="Call Phone" value={siteSettings.callUs.phone} onChange={(value) => updateNestedSetting('callUs', 'phone', value)} />
                            <Field label="WhatsApp Phone" value={siteSettings.callUs.whatsapp} onChange={(value) => updateNestedSetting('callUs', 'whatsapp', value)} />
                        </SettingsGroup>

                        <SettingsGroup title="Location" icon="fa fa-map-marker" color="#14b8a6">
                            <Field label="Location Name" value={siteSettings.location.name} onChange={(value) => updateNestedSetting('location', 'name', value)} />
                            <Field label="Location Address" value={siteSettings.location.address} onChange={(value) => updateNestedSetting('location', 'address', value)} multiline />
                            <Field label="Google Map Embed URL" type="url" value={siteSettings.location.mapUrl} onChange={(value) => updateNestedSetting('location', 'mapUrl', value)} />
                        </SettingsGroup>
                    </div>
                </form>

                <div style={{ marginTop: '24px', ...sectionStyle }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '16px' }}>
                        Quick Actions
                    </h2>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => window.open('/', '_blank')}
                            style={{
                                padding: '12px 24px',
                                background: '#62B6E8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <i className="fa fa-external-link"></i>
                            View Website
                        </button>
                        <button
                            onClick={async () => {
                                const { isConfirmed } = await confirmDelete({ title: 'Clear all data?', text: 'Are you sure? This action cannot be undone.' });
                                if (isConfirmed) {
                                    alertError('Not available', 'This feature is not implemented yet.');
                                }
                            }}
                            style={{
                                padding: '12px 24px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <i className="fa fa-trash"></i>
                            Clear All Data
                        </button>
                        <button
                            onClick={() => alertSuccess('Coming soon', 'Backup feature coming soon!')}
                            style={{
                                padding: '12px 24px',
                                background: '#62B6E8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <i className="fa fa-download"></i>
                            Backup Database
                        </button>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Settings;
